<script lang="ts" setup>
import ElInputContainer from '@/forms/ElInputContainer.vue';
import { useInput, TextualValueType, showRequiredMark, withTextualElInputProps } from '@/forms/input';
import { computed, onMounted, ref } from 'vue';

const textareaRef = ref<HTMLElement>();

const props = defineProps({
  ...withTextualElInputProps(),
  resizable: {
    type: Boolean,
    default: true,
  },
  maxCharacters: {
    type: Number,
    default: -1,
  },
  hideCharacterCounter: {
    type: Boolean,
    default: false,
  },
  textareaClass: {
    type: String,
    default: 'min-h-20',
  },
  focusOnMount: {
    type: Boolean,
    default: false,
  },
});

onMounted(() => {
  if (props.focusOnMount) {
    textareaRef.value?.focus();
  }
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: TextualValueType): void;
}>();

const computedModelValue = computed(() => props.modelValue);
const computedValidation = computed(() => [`max:${props.maxCharacters > 0 ? props.maxCharacters : 1000}`, props.validation]);

const { value, errorMessage, uuid, fieldContext } = useInput(computedModelValue, computedValidation, props.name, newValue =>
  emit('update:modelValue', newValue),
);

const currentInsertedCharNumber = computed(() => value.value?.length);

const isExceedingLimit = computed(() => {
  if (props.maxCharacters <= 0) return false;
  return (currentInsertedCharNumber.value || 0) > props.maxCharacters;
});

const computedErrorMessage = computed(() => {
  // Priority: props.errorMessage or errorMessage.value
  if (props.errorMessage || errorMessage.value) {
    return props.errorMessage || errorMessage.value;
  }
  // Fallback: character limit exceeded message
  if (isExceedingLimit.value) {
    return `Limite caratteri superato (${currentInsertedCharNumber.value}/${props.maxCharacters})`;
  }
  return false;
});

const handleEnter = (event: KeyboardEvent) => {
  event.stopPropagation();
};

const handleInput = () => {
  if (textareaRef.value) textareaRef.value.scrollTop = textareaRef.value.scrollHeight;
};
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    class="relative"
    :name="uuid"
    :label="label"
    :error-message="computedErrorMessage"
    :hidden-error-message="hiddenErrorMessage"
    :set-required-mark="showRequiredMark([validation])"
  >
    <template #default="{ error }">
      <textarea
        :id="uuid"
        ref="textareaRef"
        v-model="value"
        :name="props.name"
        :placeholder="placeholder"
        :disabled="disabled"
        class="el-input"
        :class="[
          {
            error,
            'resize-none': !resizable,
          },
          textareaClass,
        ]"
        :data-cy="`${label.trim().replaceAll(' ', '')}-textarea`"
        @blur="fieldContext.validate()"
        @input="handleInput"
        @keydown.enter="handleEnter"
      ></textarea>
      <span
        v-if="maxCharacters > 0 && !hideCharacterCounter"
        class="flex justify-end text-sm font-semibold"
        :class="currentInsertedCharNumber && currentInsertedCharNumber > maxCharacters ? 'text-red-500' : 'text-neutral-darker'"
      >
        {{ currentInsertedCharNumber }}/{{ maxCharacters }}
      </span>
    </template>
  </ElInputContainer>
</template>
